import { useEffect, useRef } from "react";
import { useAlertNotifyStore } from "../stores/use-alert-notify-store";
import { prepareAlertSound, stopAlertSound } from "../utils/play-alert-sound";
import { ALARM_PATTERN_MS } from "../utils/alarm-sounds";

/**
 * Gap between alarm repeats — exactly one pattern long, so consecutive patterns
 * TILE into a continuous loop instead of leaving a silent gap. A new alert is an
 * ALARM, not a notification: it keeps sounding until the operator actually opens
 * it, so one that fires while they're in another tab or across the room still
 * gets noticed.
 *
 * It also sidesteps Chrome's intensive throttling of background timers: a tab
 * that has played audio recently counts as audible and is exempt, so once the
 * first repeat lands the rest stay on schedule.
 */
export const ALARM_REPEAT_MS = ALARM_PATTERN_MS;

/**
 * Whether the alarm should sound right now. Pure, so the policy is testable
 * without timers: ring only for an unacknowledged, unmuted, still-new alert.
 */
export const shouldRingAlarm = (state: {
  muted: boolean;
  chimeAcknowledged: boolean;
  newIds: number[];
}): boolean => !state.muted && !state.chimeAcknowledged && state.newIds.length > 0;

/**
 * Repeat-until-acknowledged alarm for new alerts.
 *
 * This hook owns the WHOLE alarm, first ring included — the live channel and
 * the alerts queue deliberately make no sound. It stops when `acknowledgeChime()` runs
 * — which only happens on a real operator click on an alert, not on the bell
 * badge being cleared — so "seen the badge" never counts as "handled the alert".
 *
 * `onRing` should route through the same cross-tab leader election as the first
 * chime, so N open tabs still produce one sound.
 */
export const useAlertAlarm = (onRing: () => void): void => {
  // Read the callback through a ref: the interval is bound once, but the caller
  // passes a fresh closure every render (it captures the live scope key).
  const onRingRef = useRef(onRing);
  onRingRef.current = onRing;

  // Browsers refuse audio until the page has had a real user gesture. The alarm
  // mounts app-wide, so it primes its own AudioContext rather than relying on
  // the live-channel hook — that only runs on /map and the alerts page, and an
  // alarm that can ring anywhere must be able to start sound from anywhere.
  useEffect(() => {
    const prime = () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
      if (!useAlertNotifyStore.getState().muted) prepareAlertSound();
    };
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, []);

  useEffect(() => {
    // Deliberately a plain interval that re-checks the store each tick, rather
    // than an effect keyed on store state: the alarm must keep its cadence
    // across the store churn every poll causes, and re-subscribing would reset
    // the timer each time — silencing it exactly when alerts are arriving.
    let ringing = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stopLoop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    // Ring NOW, then keep the pattern repeating on its own cadence. Starting on
    // the rising edge rather than waiting for the next tick matters: the alarm
    // is the only sound left (the queue no longer chimes), so a tick-only loop
    // would delay the very first alert by up to a full pattern.
    const startLoop = () => {
      ringing = true;
      bindSilence();
      onRingRef.current();
      stopLoop();
      timer = setInterval(() => {
        if (!shouldRingAlarm(useAlertNotifyStore.getState())) return;
        onRingRef.current();
      }, ALARM_REPEAT_MS);
    };

    // A pattern runs ~10s. When the operator opens the alert (or mutes, or the
    // alert is resolved), the alarm must go quiet NOW — waiting out the rest of
    // the pattern would leave a klaxon sounding for seconds after they have
    // already responded.
    const sync = (state: Parameters<typeof shouldRingAlarm>[0]) => {
      const should = shouldRingAlarm(state);
      if (should && !ringing) startLoop();
      else if (!should && ringing) {
        ringing = false;
        unbindSilence();
        stopLoop();
        stopAlertSound();
      }
    };

    // Any click on the page silences the alarm. An operator reacting to a klaxon
    // is at the screen doing something — making them hunt for the exact alert
    // first is the alarm fighting the response it asked for. This does NOT clear
    // the "new" highlight or the bell badge: the sound stops, the work stays
    // visible. Bound only while ringing, so an ordinary click costs nothing.
    //
    // `pointerdown`, not `click`: it fires on the press, so the sound cuts the
    // instant the operator reacts rather than on release.
    //
    // Re-arming is `notify`'s existing rule — `chimeAcknowledged` flips back to
    // false only when a GENUINELY NEW alert arrives (`freshNew`). So an alert
    // that was already pending when the operator silenced it stays silent; only
    // one that arrives after the silence rings again.
    const silence = () => useAlertNotifyStore.getState().acknowledgeChime();
    const bindSilence = () => document.addEventListener("pointerdown", silence);
    const unbindSilence = () => document.removeEventListener("pointerdown", silence);

    const unsubscribe = useAlertNotifyStore.subscribe(sync);
    sync(useAlertNotifyStore.getState());

    return () => {
      stopLoop();
      unbindSilence();
      unsubscribe();
      stopAlertSound();
    };
  }, []);
};
